import compact from 'lodash/compact';
import noop from 'lodash/noop';
import { ReactElement, useMemo } from 'react';

import { Patient } from '@dreryk/egabinet-patient-frontend-api';
import { PatientMedicine } from '@dreryk/egabinet-prescription-frontend-api';
import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { PromiseVoidFunction } from '@dreryk/react-common';
import {
   AddCircleIcon,
   createDynamicColumnsWidth,
   IconWrapper,
   Padding,
   ReplaceMedicineIcon,
   TableColumnConfiguration,
   ValueRendererProps,
} from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import {
   ContinuouslyUsedMedicineButton,
   MedicineFullNameCell,
   MedicinePackage,
   MedicineSafetyIndicators,
} from '~libs.desktop.shared.medicine';

import { medicineSelectorTranslationKeys } from '../../../../translations';
import { OnMedicineSelection } from '../../PrescriptionIssuingPanel.types';
import { MedicineReplacementModalOpenAction } from '../../medicineReplacementModal/MedicineReplacementModalOpenAction';

import { AddMedicineToPrescriptionTooltip } from './AddMedicineToPrescriptionTooltip';
import { FavoriteMedicineIcon } from './FavoriteMedicinesSelectorTable/FavoriteMedicineIcon';

export const COMMERCIAL_MEDICINE_NAME_COLUMN = 'medicineFullName';

// eslint-disable-next-line max-lines-per-function
export const useMedicineSelectorTableColumns = (
   onMedicineSelection: OnMedicineSelection,
   patientId: Patient['id'],
   onMedicineProcessed: PromiseVoidFunction,
   visitDiagnoses: VisitDiagnosis[],
   removable: boolean
): TableColumnConfiguration<PatientMedicine>[] => {
   const medicineDatabaseExtendedAvailable = protectFeature('extendedMedicineDatabase');

   // eslint-disable-next-line max-lines-per-function
   return useMemo<TableColumnConfiguration<PatientMedicine>[]>(() => {
      const columnsWidths = createDynamicColumnsWidth({
         [COMMERCIAL_MEDICINE_NAME_COLUMN]: medicineDatabaseExtendedAvailable ? '65%' : '50%',
         safetyIndicators: medicineDatabaseExtendedAvailable ? '82px' : '0px',
         package: medicineDatabaseExtendedAvailable ? '35%' : '50%',
         options: '72px',
      });
      return compact([
         {
            name: COMMERCIAL_MEDICINE_NAME_COLUMN,
            accessor: noop,
            label: medicineSelectorTranslationKeys.table.commercialName,
            width: columnsWidths[COMMERCIAL_MEDICINE_NAME_COLUMN],

            renderer: (props: ValueRendererProps<PatientMedicine, PatientMedicine>): ReactElement => {
               return (
                  <MedicineFullNameCell
                     patientMedicine={props.bean}
                     onMedicineSelection={onMedicineSelection}
                     visitDiagnoses={visitDiagnoses}
                  />
               );
            },
         },
         medicineDatabaseExtendedAvailable
            ? {
                 name: 'safetyIndicators',
                 accessor: noop,
                 width: columnsWidths.safetyIndicators,

                 renderer: (props: ValueRendererProps<PatientMedicine>) => (
                    <MedicineSafetyIndicators medicineId={props.bean.medicine.id} />
                 ),
                 sortingDisabled: true,
              }
            : undefined,
         {
            name: 'package',
            accessor: noop,
            label: medicineSelectorTranslationKeys.table.package,
            width: columnsWidths.package,
            renderer: (props: ValueRendererProps<PatientMedicine, PatientMedicine>): ReactElement => (
               <MedicinePackage
                  form={props.bean.medicine.form}
                  dose={props.bean.medicine.dose}
                  packageContent={props.bean.medicine.packageContent}
                  valid={props.bean.medicine.valid}
               />
            ),
            sortingDisabled: true,
         },
         {
            name: 'options',
            accessor: noop,
            label: '',

            renderer: (props: ValueRendererProps<PatientMedicine, PatientMedicine>): ReactElement => {
               const valid = props.bean.medicine.valid !== false;
               return (
                  <>
                     {valid ? (
                        <ContinuouslyUsedMedicineButton
                           medicineId={props.bean.medicine.id}
                           patientId={patientId}
                           continuouslyUsedMedicineId={props.bean.patientContinuouslyUsedMedicineId}
                           medicineName={props.bean.medicine.fullName}
                           medicineValid={valid}
                           onMedicineProcessed={onMedicineProcessed}
                        />
                     ) : null}
                     {valid || removable ? (
                        <FavoriteMedicineIcon
                           medicineId={props.bean.medicine.id}
                           medicineValid={valid}
                           favorite={Boolean(props.bean.favorite)}
                           onMedicineProcessed={onMedicineProcessed}
                        />
                     ) : null}
                     {valid ? (
                        <AddMedicineToPrescriptionTooltip>
                           <IconWrapper onClick={() => onMedicineSelection(props.bean)} testId={'addToPrescription'}>
                              <AddCircleIcon />
                           </IconWrapper>
                        </AddMedicineToPrescriptionTooltip>
                     ) : null}
                     {removable && !valid ? (
                        <MedicineReplacementModalOpenAction
                           withdrawnMedicine={props.bean.medicine}
                           patientId={patientId}
                           medicineId={props.bean.medicine.id}
                           visitDiagnoses={visitDiagnoses}
                           source={'favorite'}
                        >
                           {({ openModal }) => (
                              <IconWrapper testId={'replaceMedicine'} onClick={openModal}>
                                 <ReplaceMedicineIcon />
                              </IconWrapper>
                           )}
                        </MedicineReplacementModalOpenAction>
                     ) : null}
                  </>
               );
            },
            width: columnsWidths.options,
            sortingDisabled: true,
            padding: Padding.NONE,
         },
      ]);
   }, [
      medicineDatabaseExtendedAvailable,
      onMedicineProcessed,
      onMedicineSelection,
      patientId,
      visitDiagnoses,
      removable,
   ]);
};
